import styled, { keyframes, css } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { CommittedDestinationIcon, GreenEarth, RightArrowIcon, SoftMobilityIcon } from '@net0-calc/common/assets/svg';
import { RedAccentHeading } from 'components';
const translationPrefix = 'personal.pages.result.tipsToReduce';

export const TipsToReduce = () => {
  const { t, i18n } = useTranslation();
  const resolvedLanguage = i18n.resolvedLanguage || 'en';

  return (
    <Root>
      <InfoWrapper>
        <RedAccentHeading>
          <Title>
            <EmojiContainer>💡</EmojiContainer> {t(translationPrefix + '.title')}
          </Title>
        </RedAccentHeading>
        <div>
          <Paragraph>{t(translationPrefix + '.description.paragraph1')}</Paragraph>
          <Paragraph>
            {t(translationPrefix + '.description.paragraph2')}
            <ParagraphLink
              target="_blank"
              href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/39479/green-access-guide-to-the-principality-of-monaco`}>
              {' ' + t(translationPrefix + '.description.linkTextForP2')}
            </ParagraphLink>
          </Paragraph>
          <Paragraph>{t(translationPrefix + '.description.paragraph3')}</Paragraph>
          <Paragraph>
            {t(translationPrefix + '.description.paragraph4')}
            <ParagraphLink
              target="_blank"
              href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/38669/soft-mobility-in-monaco`}>
              {' ' + t(translationPrefix + '.description.linkTextForP4')}
            </ParagraphLink>
          </Paragraph>
          <Paragraph>{t(translationPrefix + '.description.paragraph5')}</Paragraph>
        </div>
      </InfoWrapper>
      <LinksWrapper>
        <Link
          href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/26450/a-destination-with-values`}
          target="_blank">
          <StyledCommittedDestinationIcon />
          <LinkText>{t('events.pages.result.tipsToReduce.links.guide')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
        <Link
          href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/37823/responsible-tourism-white-paper`}
          target="_blank">
          <StyledGreenEarth fill="var(--color2)" />
          <LinkText>{t('events.pages.result.tipsToReduce.links.whitepapert')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
        <Link
          href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/38669/soft-mobility-in-monaco`}
          target="_blank">
          <StyledSoftMobilityIcon />
          <LinkText>{t('events.pages.result.tipsToReduce.links.hotels')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
      </LinksWrapper>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  margin-bottom: 40px;
  order: 3;
  @media ${DEVICE.laptop} {
    margin-bottom: 60px;
  }
`;

const InfoWrapper = styled.div`
  max-width: 845px;
`;

const Title = styled.div`
  ${TYPOGRAPHY.bodyMedium20}
  color: var(--color6);
  margin-bottom: 16px;
  display: flex;
  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold32}
    margin-bottom: 32px;
  }
`;

const Paragraph = styled.div`
  $${TYPOGRAPHY.boydRegular20}
  color: var(--color6);
  width: 100%;
  margin-bottom: 32px;
  &:last-of-type {
    margin-bottom: 24px;
  }
`;

const ParagraphLink = styled.a`
  text-decoration: none;
  color: var(--color2);
`;

const LinksWrapper = styled.div`
  max-width: 1117px;
  display: flex;
  flex-direction: column;

  @media ${DEVICE.laptop} {
    flex-direction: row;
  }
`;

const Link = styled.a`
  ${TYPOGRAPHY.bodyMedium16}
  max-width: 400px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  border-radius: 12px;
  border: 1px solid var(--color10);
  background: var(--color5);
  color: var(--color6);
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:not(:last-child) {
    margin: 0 0 12px;
  }

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium16}
    width: auto;
    padding: 20px;

    &:not(:last-child) {
      margin: 0 16px 0 0;
    }
  }

  &:hover {
    border-color: var(--color2);
    box-shadow: 0 4px 16px rgba(206, 17, 38, 0.1);
    transform: translateY(-2px);
    color: var(--color2);
  }
`;

const LinkText = styled.p`
  flex: 1;
`;

const iconsCommonCSS = css`
  margin-right: 16px;
`;

const StyledCommittedDestinationIcon = styled(CommittedDestinationIcon)`
  ${iconsCommonCSS}
`;

const StyledSoftMobilityIcon = styled(SoftMobilityIcon)`
  ${iconsCommonCSS}
`;
const StyledGreenEarth = styled(GreenEarth)`
  ${iconsCommonCSS}
`;

const StyledRightArrowIcon = styled(RightArrowIcon)`
  margin-left: 16px;
  stroke: var(--color3);
`;

const emojiPulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
`;

const EmojiContainer = styled.div`
  cursor: pointer;
  margin-right: 8px;
  &:hover {
    animation: ${emojiPulseAnimation} 0.2s ease-in normal;
  }
`;
